import { useRef, useState } from 'preact/hooks';

import { isSignedLink, SIGNED_LINK_MARKER } from '../image-links';

/**
 * A customer picture whose signed link expires after an hour. When it fails to load, ask once for a fresh
 * link (`onRefresh` stores it and returns it; the parent passes it back as `src`); if that link or the
 * request fails too, show a placeholder. A link is never tried twice.
 */
export function RefreshableImage({ src, alt, loading, onRefresh }: {
  src: string;
  alt: string;
  loading?: 'lazy' | 'eager';
  onRefresh: () => Promise<string | null>;
}) {
  const tried = useRef(new Set<string>());
  const [failed, setFailed] = useState<string | null>(null);

  if (failed === src) {
    return <div class="chuck-image-unavailable" role="img" aria-label="Picture unavailable">Picture unavailable</div>;
  }

  const onError = async () => {
    if (tried.current.has(src)) {
      setFailed(src);
      return;
    }
    tried.current.add(src);
    let fresh: string | null = null;
    try {
      fresh = await onRefresh();
    } catch {
      // shown as unavailable below
    }
    if (!fresh || fresh === src) setFailed(src);
    else tried.current.add(fresh);
  };

  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      data-picture-link={isSignedLink(src) ? SIGNED_LINK_MARKER : undefined}
      onError={() => void onError()}
    />
  );
}
